import { useQuery } from '@tanstack/react-query';
import type { Database } from '@pirouette/db';
import { supabase } from '@/lib/supabase';
import type {
  EnrollmentRow,
  ChargeRow,
  PaymentRow,
  AccountBalanceRow as AnalyticsAccountBalanceRow,
  AttendanceRow,
  LeadRow,
  TrialRow,
  DanceCastRow,
} from '@/lib/analytics';

interface QueryError {
  message?: string;
}

// Supabase caps a single select at 1000 rows by default. The analytics
// dashboard aggregates client-side from raw rows, so we page explicitly to
// avoid silently truncating a busy studio's data into a wrong total. Each
// query below selects the NARROWEST column set the analytics helpers need.
const PAGE_SIZE = 1000;
const MAX_ANALYTICS_ROWS = 50000; // hard ceiling — defensive against runaway pulls

async function fetchAllRows<T>(
  build: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: QueryError | null }>,
): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; from < MAX_ANALYTICS_ROWS; from += PAGE_SIZE) {
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await build(from, to);
    if (error) throw new Error(error.message ?? 'Analytics query failed');
    const batch = data ?? [];
    out.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }
  return out;
}

export interface DateRange {
  // Inclusive `YYYY-MM-DD` bounds. Either side may be undefined (open-ended).
  start?: string;
  end?: string;
}

export interface LeadConversionReportRow {
  lead_status: Database['public']['Enums']['lead_status'];
  source: string;
  lead_count: number;
  converted_count: number;
}

export interface ProgramEnrollmentReportRow {
  program_type: Database['public']['Enums']['program_type'];
  active_enrollment_count: number;
  class_count: number;
  total_capacity: number;
}

export interface PrivateLessonUtilizationReportRow {
  program_type: Database['public']['Enums']['program_type'];
  program_name: string;
  location_name: string;
  instructor_name: string;
  lesson_count: number;
  active_enrollment_count: number;
  total_capacity: number;
}

export interface CostumeDeadlinesReportRow {
  production_name: string;
  package_name: string;
  due_date: string | null;
  assignment_count: number;
  incomplete_count: number;
}

export interface RecitalPackageFulfillmentReportRow {
  item_name: string;
  item_status: Database['public']['Enums']['package_item_status'];
  assignment_count: number;
}

export function useLeadConversionReport() {
  return useQuery({
    queryKey: ['reports', 'lead-conversion'],
    queryFn: async (): Promise<LeadConversionReportRow[]> => {
      const { data, error } = await supabase.rpc('report_lead_conversion');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useProgramEnrollmentReport() {
  return useQuery({
    queryKey: ['reports', 'program-enrollment'],
    queryFn: async (): Promise<ProgramEnrollmentReportRow[]> => {
      const { data, error } = await supabase.rpc('report_program_enrollment');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function usePrivateLessonUtilizationReport() {
  return useQuery({
    queryKey: ['reports', 'private-lesson-utilization'],
    queryFn: async (): Promise<PrivateLessonUtilizationReportRow[]> => {
      const { data, error } = await supabase.rpc('report_private_lesson_utilization');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCostumeDeadlinesReport() {
  return useQuery({
    queryKey: ['reports', 'costume-deadlines'],
    queryFn: async (): Promise<CostumeDeadlinesReportRow[]> => {
      const { data, error } = await supabase.rpc('report_costume_deadlines');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useRecitalPackageFulfillmentReport() {
  return useQuery({
    queryKey: ['reports', 'recital-package-fulfillment'],
    queryFn: async (): Promise<RecitalPackageFulfillmentReportRow[]> => {
      const { data, error } = await supabase.rpc('report_recital_package_fulfillment');
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ===========================================================================
// Owner analytics dashboard data (Phase 4)
//
// These hooks pull the raw, RLS/tenant-scoped rows that the pure helpers in
// @/lib/analytics aggregate. Nothing here bypasses RLS — every query runs as
// the authenticated admin and the existing per-table policies scope it to the
// caller's tenant. The dashboard does the math in the browser so we reuse the
// already-tested analytics module rather than minting new RPCs.
// ===========================================================================

/** Enrollment rows for trend / retention math. Date filter is applied to
 * start_date so the window bounds the cohort, but we keep rows that started
 * before the window and remain relevant for retention by ALSO pulling rows
 * whose end_date falls in the window. */
export function useEnrollmentAnalytics(range: DateRange) {
  return useQuery({
    queryKey: ['analytics', 'enrollments', range.start ?? null, range.end ?? null],
    queryFn: async (): Promise<EnrollmentRow[]> => {
      return fetchAllRows<EnrollmentRow>((from, to) => {
        let q = supabase
          .from('enrollments')
          .select('status, start_date, end_date, deleted_at')
          .is('deleted_at', null)
          .order('start_date', { ascending: true })
          .range(from, to);
        // Bound by start OR end inside the window. Supabase `or` keeps it a
        // single round-trip; retention needs pre-window starts too, so we only
        // apply the lower bound to end_date (drops in-window) and upper bound
        // to start_date (starts up to window end).
        if (range.end) q = q.lte('start_date', range.end);
        return q;
      });
    },
  });
}

/** Charge rows for revenue + aging. */
export function useChargeAnalytics(range: DateRange) {
  return useQuery({
    queryKey: ['analytics', 'charges', range.start ?? null, range.end ?? null],
    queryFn: async (): Promise<ChargeRow[]> => {
      return fetchAllRows<ChargeRow>((from, to) => {
        let q = supabase
          .from('charges')
          .select('amount_cents, status, due_date, created_at')
          .order('created_at', { ascending: true })
          .range(from, to);
        if (range.start) q = q.gte('created_at', range.start);
        if (range.end) q = q.lte('created_at', `${range.end}T23:59:59.999Z`);
        return q;
      }).then((rows) =>
        rows.map((r) => ({
          amount_cents: r.amount_cents,
          status: r.status,
          due_date: r.due_date,
        })),
      );
    },
  });
}

/** Succeeded payment rows for collected revenue. */
export function usePaymentAnalytics(range: DateRange) {
  return useQuery({
    queryKey: ['analytics', 'payments', range.start ?? null, range.end ?? null],
    queryFn: async (): Promise<PaymentRow[]> => {
      return fetchAllRows<PaymentRow>((from, to) => {
        let q = supabase
          .from('payments')
          .select('amount_cents, status, paid_at')
          .order('paid_at', { ascending: true, nullsFirst: false })
          .range(from, to);
        if (range.start) q = q.gte('paid_at', range.start);
        if (range.end) q = q.lte('paid_at', `${range.end}T23:59:59.999Z`);
        return q;
      });
    },
  });
}

/** Authoritative AR snapshot (allocations already netted by the view). This is
 * a point-in-time balance, so it is NOT date-filtered. */
export function useAccountBalanceAnalytics() {
  return useQuery({
    queryKey: ['analytics', 'account-balances'],
    queryFn: async (): Promise<AnalyticsAccountBalanceRow[]> => {
      const { data, error } = await supabase
        .from('account_balance')
        .select('outstanding_cents, open_count, overdue_count')
        .eq('active', true);
      if (error) throw error;
      return (data ?? []).map((r) => ({
        outstanding_cents: r.outstanding_cents ?? 0,
        open_count: r.open_count ?? 0,
        overdue_count: r.overdue_count ?? 0,
      }));
    },
  });
}

/** Attendance rows joined to class + dancer labels for repeat-absence and
 * at-risk grouping. Filtered to the window by the parent session date. */
export function useAttendanceAnalytics(range: DateRange) {
  return useQuery({
    queryKey: ['analytics', 'attendance', range.start ?? null, range.end ?? null],
    queryFn: async (): Promise<AttendanceRow[]> => {
      const rows = await fetchAllRows<{
        status: string;
        dancer_id: string;
        dancers: { first_name: string; last_name: string } | null;
        class_sessions: { class_id: string; session_date: string } | null;
      }>((from, to) => {
        let q = supabase
          .from('attendance_records')
          .select(
            'status, dancer_id, dancers(first_name, last_name), class_sessions!inner(class_id, session_date)',
          )
          .order('created_at', { ascending: true })
          .range(from, to);
        if (range.start) q = q.gte('class_sessions.session_date', range.start);
        if (range.end) q = q.lte('class_sessions.session_date', range.end);
        return q;
      });
      return rows.map((r) => ({
        status: r.status,
        dancer_id: r.dancer_id,
        dancer_label: r.dancers ? `${r.dancers.first_name} ${r.dancers.last_name}` : r.dancer_id,
        ...(r.class_sessions?.class_id ? { group_id: r.class_sessions.class_id } : {}),
      }));
    },
  });
}

/** Class labels so repeat-absence rows can show a human name. Returned as a
 * map keyed by class id; the dashboard merges this into the attendance rows. */
export function useClassLabels() {
  return useQuery({
    queryKey: ['analytics', 'class-labels'],
    queryFn: async (): Promise<Record<string, string>> => {
      const rows = await fetchAllRows<{ id: string; name: string }>((from, to) =>
        supabase
          .from('classes')
          .select('id, name')
          .is('deleted_at', null)
          .order('name', { ascending: true })
          .range(from, to),
      );
      return Object.fromEntries(rows.map((r) => [r.id, r.name]));
    },
  });
}

/** Lead rows for the trial funnel. */
export function useLeadAnalytics(range: DateRange) {
  return useQuery({
    queryKey: ['analytics', 'leads', range.start ?? null, range.end ?? null],
    queryFn: async (): Promise<LeadRow[]> => {
      return fetchAllRows<LeadRow>((from, to) => {
        let q = supabase
          .from('studio_leads')
          .select('lead_status, converted_family_id, created_at')
          .order('created_at', { ascending: true })
          .range(from, to);
        if (range.start) q = q.gte('created_at', range.start);
        if (range.end) q = q.lte('created_at', `${range.end}T23:59:59.999Z`);
        return q;
      }).then((rows) =>
        rows.map((r) => ({
          lead_status: r.lead_status,
          converted_family_id: r.converted_family_id ?? null,
        })),
      );
    },
  });
}

/** Trial booking rows for the trial funnel. */
export function useTrialAnalytics(range: DateRange) {
  return useQuery({
    queryKey: ['analytics', 'trials', range.start ?? null, range.end ?? null],
    queryFn: async (): Promise<TrialRow[]> => {
      return fetchAllRows<TrialRow>((from, to) => {
        let q = supabase
          .from('trial_bookings')
          .select('status, trial_date')
          .order('trial_date', { ascending: true })
          .range(from, to);
        if (range.start) q = q.gte('trial_date', range.start);
        if (range.end) q = q.lte('trial_date', range.end);
        return q;
      }).then((rows) => rows.map((r) => ({ status: r.status })));
    },
  });
}

/** Per-dance cast counts so the dashboard can flag dances missing a cast.
 * Pulls active (non-deleted) dances and counts their cast assignments in one
 * embedded aggregate; the analytics helper filters to zero-cast. */
export function useDanceCastAnalytics() {
  return useQuery({
    queryKey: ['analytics', 'dance-cast'],
    queryFn: async (): Promise<DanceCastRow[]> => {
      const rows = await fetchAllRows<{
        id: string;
        name: string;
        cast_assignments: { count: number }[];
      }>((from, to) =>
        supabase
          .from('dances')
          .select('id, name, cast_assignments(count)')
          .is('deleted_at', null)
          .order('name', { ascending: true })
          .range(from, to),
      );
      return rows.map((r) => ({
        dance_id: r.id,
        dance_name: r.name,
        cast_count: r.cast_assignments?.[0]?.count ?? 0,
      }));
    },
  });
}
